import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ThankYou() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {
        // keep trying a few times
      }
      tries += 1;
      if (tries >= 8) {
        setState("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="relative min-h-screen grain overflow-hidden bg-[var(--charcoal)] text-[var(--cream)] flex items-center justify-center px-6">
      <div className="beam-glow pointer-events-none absolute inset-0" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 max-w-xl text-center"
        data-testid="thank-you-page"
      >
        <p className="overline text-[var(--gold-soft)] justify-center flex items-center gap-3">
          <span className="h-px w-8 bg-[var(--gold)]" />
          Beam Concierge
          <span className="h-px w-8 bg-[var(--gold)]" />
        </p>

        {state === "checking" && (
          <div className="mt-10 flex flex-col items-center gap-4">
            <Loader2 className="animate-spin text-[var(--gold-soft)]" size={34} />
            <p className="text-[var(--cream)]/70">Confirming your payment…</p>
          </div>
        )}

        {state === "paid" && (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 180, damping: 14 }}
              className="mx-auto mt-8 flex h-20 w-20 items-center justify-center rounded-full border border-[var(--gold)]"
            >
              <Check className="text-[var(--gold-soft)]" size={38} strokeWidth={1.6} />
            </motion.div>
            <h1 className="mt-8 serif text-[clamp(2.4rem,6vw,4rem)] leading-tight">
              Welcome to the <span className="italic gold-shimmer">Beam.</span>
            </h1>
            <p className="mt-5 text-lg leading-relaxed text-[var(--cream)]/75">
              {info.client_name ? `Thank you, ${info.client_name}. ` : "Thank you. "}
              Your payment for <span className="text-[var(--gold-soft)]">{info.label || "your service"}</span> is confirmed.
              Consider this your five-star check-in — we'll be in touch shortly to begin building your reputation.
            </p>
          </>
        )}

        {state === "pending" && (
          <>
            <Sparkles className="mx-auto mt-8 text-[var(--gold-soft)]" size={34} />
            <h1 className="mt-6 serif text-4xl">Almost there.</h1>
            <p className="mt-4 text-[var(--cream)]/75">
              Your payment is processing. You'll receive a confirmation by email shortly —
              no need to pay again.
            </p>
          </>
        )}

        {state === "error" && (
          <>
            <h1 className="mt-10 serif text-4xl">We couldn't confirm this yet.</h1>
            <p className="mt-4 text-[var(--cream)]/75">
              If you completed payment, a confirmation is on its way. Otherwise, please
              reach out and we'll help right away.
            </p>
          </>
        )}

        <Link
          to="/"
          data-testid="thank-you-home"
          className="mt-10 inline-flex items-center gap-2 border border-[var(--gold)] px-7 py-3.5 text-sm text-[var(--cream)] hover:bg-[var(--gold)]/10 transition-colors duration-400"
        >
          Return to Beam Concierge
        </Link>
      </motion.div>
    </div>
  );
}
